import { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { getSchoolNetworks, deleteSchoolNetwork } from '@/api/school-networks';
import type { SchoolNetwork } from '@/types/school-network';
import { DataTable, Column } from '@/components/ui/data-table';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Plus, Eye, Trash2, Network, Server } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

type TypeFilter = 'all' | 'physical' | 'virtual';

export function SchoolNetworksPage() {
  const navigate = useNavigate();
  const { hasRole } = useAuth();
  const [networks, setNetworks] = useState<SchoolNetwork[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchValue, setSearchValue] = useState('');
  const [typeFilter, setTypeFilter] = useState<TypeFilter>('all');
  const [sortBy, setSortBy] = useState<string>('name');
  const [sortOrder, setSortOrder] = useState<'ASC' | 'DESC'>('ASC');
  const [deleteNetwork, setDeleteNetwork] = useState<SchoolNetwork | null>(null);
  const [deleting, setDeleting] = useState(false);

  const isAdmin = hasRole('ROLE_ADMIN') || hasRole('ROLE_SUPERADMIN');

  useEffect(() => {
    fetchNetworks();
  }, []);

  const fetchNetworks = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getSchoolNetworks();
      setNetworks(data);
    } catch (err) {
      setError('Erreur lors du chargement des reseaux');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (key: string) => {
    if (sortBy === key) {
      setSortOrder(sortOrder === 'ASC' ? 'DESC' : 'ASC');
    } else {
      setSortBy(key);
      setSortOrder('ASC');
    }
  };

  const handleDelete = async () => {
    if (!deleteNetwork) return;

    try {
      setDeleting(true);
      await deleteSchoolNetwork(deleteNetwork.id);
      setNetworks(networks.filter((n) => n.id !== deleteNetwork.id));
      setDeleteNetwork(null);
    } catch (err) {
      console.error(err);
      setError('Erreur lors de la suppression du reseau');
    } finally {
      setDeleting(false);
    }
  };

  const filteredAndSortedNetworks = useMemo(() => {
    let result = [...networks];

    // Filter by type
    if (typeFilter !== 'all') {
      result = result.filter((n) => n.type === typeFilter);
    }

    // Filter
    if (searchValue) {
      const search = searchValue.toLowerCase();
      result = result.filter(
        (n) =>
          n.name.toLowerCase().includes(search) ||
          (n.description && n.description.toLowerCase().includes(search))
      );
    }

    // Sort
    result.sort((a, b) => {
      const aValue = a[sortBy as keyof SchoolNetwork];
      const bValue = b[sortBy as keyof SchoolNetwork];

      if (aValue === null || aValue === undefined) return 1;
      if (bValue === null || bValue === undefined) return -1;

      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return sortOrder === 'ASC'
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }

      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sortOrder === 'ASC' ? aValue - bValue : bValue - aValue;
      }

      return 0;
    });

    return result;
  }, [networks, searchValue, typeFilter, sortBy, sortOrder]);

  const columns: Column<SchoolNetwork>[] = [
    {
      key: 'name',
      header: 'Nom',
      sortable: true,
      render: (network) => (
        <div>
          <Link to={`/school-networks/${network.id}`} className="font-medium text-gray-900 hover:text-primary">
            {network.name}
          </Link>
          {network.type === 'physical' && network.v2Host && (
            <p className="text-xs text-muted-foreground font-mono">{network.v2Host}</p>
          )}
        </div>
      ),
    },
    {
      key: 'type',
      header: 'Type',
      sortable: true,
      render: (network) =>
        network.type === 'physical' ? (
          <Badge className="bg-violet-100 text-violet-700 hover:bg-violet-100 gap-1">
            <Server className="h-3 w-3" />
            Physique
          </Badge>
        ) : (
          <Badge variant="secondary" className="gap-1">
            <Network className="h-3 w-3" />
            Virtuel
          </Badge>
        ),
    },
    {
      key: 'description',
      header: 'Description',
      render: (network) => (
        <span className="text-gray-600 text-sm truncate max-w-xs block">
          {network.description || '-'}
        </span>
      ),
    },
    {
      key: 'schools',
      header: 'Écoles',
      render: (network) => {
        if (network.schoolCount === 0) {
          return <span className="text-muted-foreground text-sm">-</span>;
        }
        const remaining = network.schoolCount - network.schoolsPreview.length;
        return (
          <div className="flex flex-wrap gap-1 max-w-md">
            {network.schoolsPreview.map((school) => (
              <Link key={school.code} to={`/schools/${school.code}`}>
                <Badge
                  variant="outline"
                  className="text-xs font-normal hover:bg-primary hover:text-primary-foreground cursor-pointer transition-colors"
                >
                  {school.name}
                </Badge>
              </Link>
            ))}
            {remaining > 0 && (
              <Badge variant="secondary" className="text-xs">
                +{remaining}
              </Badge>
            )}
          </div>
        );
      },
    },
    {
      key: 'actions',
      header: 'Actions',
      className: 'text-right',
      render: (network) => (
        <div className="flex items-center justify-end gap-2">
          <Button
            variant="ghost"
            size="icon-sm"
            onClick={() => navigate(`/school-networks/${network.id}`)}
            title="Voir"
          >
            <Eye className="h-4 w-4" />
          </Button>
          {isAdmin && network.type === 'virtual' && (
            <Button
              variant="ghost"
              size="icon-sm"
              onClick={() => setDeleteNetwork(network)}
              title="Supprimer"
              className="text-red-600 hover:text-red-700 hover:bg-red-50"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="space-y-2">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary">
              <Network className="h-5 w-5" />
            </div>
            <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-foreground">
              Réseaux d'écoles
            </h1>
          </div>
          <p className="text-sm md:text-base text-muted-foreground pl-[52px]">
            Gérez les réseaux pour regrouper vos écoles
          </p>
        </div>

        <Button asChild>
          <Link to="/school-networks/new">
            <Plus className="h-4 w-4" />
            Nouveau reseau
          </Link>
        </Button>
      </div>

      {/* Error message */}
      {error && (
        <div className="rounded-lg bg-red-50 border border-red-200 p-4 text-red-700">
          {error}
        </div>
      )}

      {/* Type filter */}
      <div className="flex gap-2">
        {(
          [
            { value: 'all', label: 'Tous' },
            { value: 'physical', label: 'Physiques' },
            { value: 'virtual', label: 'Virtuels' },
          ] as Array<{ value: TypeFilter; label: string }>
        ).map((tab) => (
          <Button
            key={tab.value}
            variant={typeFilter === tab.value ? 'default' : 'outline'}
            size="sm"
            onClick={() => setTypeFilter(tab.value)}
          >
            {tab.label}
          </Button>
        ))}
      </div>

      {/* Data Table */}
      <DataTable
        columns={columns}
        data={filteredAndSortedNetworks}
        loading={loading}
        searchValue={searchValue}
        onSearchChange={setSearchValue}
        searchPlaceholder="Rechercher par nom ou description..."
        sortBy={sortBy}
        sortOrder={sortOrder}
        onSort={handleSort}
        totalItems={filteredAndSortedNetworks.length}
        emptyMessage="Aucun reseau trouve"
      />

      {/* Delete confirmation dialog */}
      <AlertDialog open={!!deleteNetwork} onOpenChange={() => setDeleteNetwork(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer le reseau ?</AlertDialogTitle>
            <AlertDialogDescription>
              Etes-vous sur de vouloir supprimer le reseau{' '}
              <strong>{deleteNetwork?.name}</strong> ?
              <br />
              <br />
              Les écoles associées ne seront pas supprimées, mais seront retirées du
              réseau.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700 focus:ring-red-600"
            >
              {deleting ? 'Suppression...' : 'Supprimer'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
